import React, { useEffect, useState } from 'react';
import { googleListingApi, GoogleRating } from '../../services/googleListing';
import { toSafeHref } from '../../utils/url';
import GoogleMapsAttribution from './GoogleMapsAttribution';

export const GOOGLE_REVIEW_POLICY_URL =
  'https://support.google.com/contributionpolicy/answer/7422880';

interface GoogleRatingPanelProps {
  providerId: number;
}

// The provider's Google rating, fetched live from Google each time it's shown. Google's
// terms don't allow storing it or building on it, so it stays out of the CLOUT score.
// Renders nothing until it loads, or if Google has no rating to give.
const GoogleRatingPanel: React.FC<GoogleRatingPanelProps> = ({ providerId }) => {
  const [rating, setRating] = useState<GoogleRating | null>(null);

  useEffect(() => {
    let cancelled = false;
    setRating(null);
    googleListingApi
      .getRating(providerId)
      .then((result) => {
        if (!cancelled) setRating(result);
      })
      .catch(() => {
        // No listing, Google unavailable, or rate limited: show nothing
      });
    return () => {
      cancelled = true;
    };
  }, [providerId]);

  if (!rating) return null;

  const mapsHref = toSafeHref(rating.google_maps_uri);
  const count = rating.user_rating_count;

  return (
    <section
      aria-label="Google rating"
      className="border border-[#dadce0] bg-white rounded-lg px-[10px] pt-[10px] pb-3 font-['Open_Sans']"
    >
      <GoogleMapsAttribution />
      <div className="mt-2 text-sm text-[#1f1f1f]">
        {rating.rating != null ? (
          <p>
            <span className="text-lg font-semibold">{rating.rating.toFixed(1)}</span>
            <span className="text-[#5e5e5e]">
              {' '}
              out of 5 from {count} Google {count === 1 ? 'review' : 'reviews'}
            </span>
          </p>
        ) : (
          <p className="text-[#5e5e5e]">No Google reviews yet</p>
        )}
        {rating.name && <p className="text-xs text-[#5e5e5e] mt-1">Listing: {rating.name}</p>}
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {mapsHref && (
          <a
            href={mapsHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#1a73e8] hover:underline"
          >
            View on Google Maps
          </a>
        )}
        <a
          href={GOOGLE_REVIEW_POLICY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#1a73e8] hover:underline"
        >
          Google review policy
        </a>
      </div>
      <p className="mt-2 text-xs text-[#5e5e5e]">
        From Google, not CLOUT. It isn't part of the CLOUT Score.
      </p>
    </section>
  );
};

export default GoogleRatingPanel;
